import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, MenuItem, Button, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Chip, Menu, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import api from '../../api';

const AttendanceReportList = () => {
  const formatLocal = (d) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const today = new Date();
  let start, end;
  
  if (today.getDate() === 1) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    start = formatLocal(new Date(today.getFullYear(), today.getMonth() - 1, 1));
    end = formatLocal(prevMonth);
  } else {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    start = formatLocal(new Date(today.getFullYear(), today.getMonth(), 1));
    end = formatLocal(yesterday);
  }

  const [filters, setFilters] = useState({ emp_id: '', start_date: start, end_date: end, department_id: '', designation_id: '', status: '' });
  const [dropdowns, setDropdowns] = useState({ departments: [], designations: [] });
  
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [columns, setColumns] = useState({ emp_id: true, name: true, dept: true, l1: false, date: true, shift: true, checkin: true, checkout: true, twh: false, toh: false, status: true });
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    api.get('/correction-dashboard/filters-data').then(res => setDropdowns(res.data)).catch(console.error);
  }, []);

  useEffect(() => { fetchReport(); }, [page, rowsPerPage]);

  const fetchReport = async () => {
    const query = new URLSearchParams({ ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')), page, rowsPerPage }).toString();
    try {
      const res = await api.get(`/attendance-report/list?${query}`);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
  };

  const handleExport = () => {
    const activeCols = Object.keys(columns).filter(k => columns[k]).join(',');
    const queryParams = new URLSearchParams({ ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')), columns: activeCols }).toString();
    window.open(`http://${window.location.hostname}:8000/api/v1/attendance-report/export?${queryParams}`, '_blank');
  };

  const getDisplayDateText = () => {
    if (filters.start_date && filters.end_date) {
      const s = new Date(filters.start_date);
      const e = new Date(filters.end_date);
      if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
        return s.toLocaleString('default', { month: 'long', year: 'numeric' });
      }
      return `${filters.start_date} to ${filters.end_date}`;
    }
    return 'All Time';
  };

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
        <TextField size="small" label="Emp ID / Name" value={filters.emp_id} onChange={e => setFilters({...filters, emp_id: e.target.value})} sx={{ minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField select size="small" label="Department" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.department_id} onChange={e => setFilters({...filters, department_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
          {dropdowns.departments.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.department_name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Designation" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.designation_id} onChange={e => setFilters({...filters, designation_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
          {dropdowns.designations.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.designation_name}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Status" value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} sx={{ minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <Button variant="contained" onClick={() => { setPage(0); fetchReport(); }} disableElevation sx={{ bgcolor: '#1976d2', py: 1 }}>Search</Button>
        <Button variant="outlined" startIcon={<GetAppIcon />} color="success" onClick={handleExport} sx={{ ml: 'auto' }}>Export</Button>
        <Button variant="outlined" startIcon={<FilterListIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>Columns</Button>
      </Paper>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pl: 0.5, fontWeight: 500 }}>
        Showing data for: <strong style={{ color: '#10394f' }}>{getDisplayDateText()}</strong> (Active Employees Only)
      </Typography>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <FormGroup sx={{ p: 2 }}>
          {Object.keys(columns).map(key => (
             <FormControlLabel key={key} control={<Checkbox checked={columns[key]} onChange={e => setColumns({...columns, [key]: e.target.checked})} />} label={key.toUpperCase()} />
          ))}
        </FormGroup>
      </Menu>

      <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f9fafb' }}>
            <TableRow>
              {columns.emp_id && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Emp ID</TableCell>}
              {columns.name && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Name</TableCell>}
              {columns.dept && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Department</TableCell>}
              {columns.l1 && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>L1 Manager</TableCell>}
              {columns.date && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Date</TableCell>}
              {columns.shift && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Shift</TableCell>}
              {columns.checkin && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Check-In</TableCell>}
              {columns.checkout && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Check-Out</TableCell>}
              {columns.twh && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>TWH</TableCell>}
              {columns.toh && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>TOH</TableCell>}
              {columns.status && <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Status</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? <TableRow><TableCell colSpan={11} align="center" sx={{ py: 3 }}>No records found.</TableCell></TableRow> : data.map((row, index) => (
              <TableRow key={`${row.emp_id}-${row.formatted_date}-${index}`} hover>
                {columns.emp_id && <TableCell sx={{ fontWeight: 'bold', color: '#444' }}>{row.emp_id}</TableCell>}
                {columns.name && <TableCell sx={{ color: '#444' }}>{row.name}</TableCell>}
                {columns.dept && <TableCell sx={{ color: '#555' }}>{row.department_name || '--'}</TableCell>}
                {columns.l1 && <TableCell sx={{ color: '#555' }}>{row.l1_manager || '--'}</TableCell>}
                {columns.date && <TableCell sx={{ color: '#555' }}>{row.formatted_date}</TableCell>}
                {columns.shift && <TableCell sx={{ color: '#555' }}>{row.shift || '--'}</TableCell>}
                {columns.checkin && <TableCell sx={{ color: '#2e7d32', fontWeight: 500 }}>{row.checkin || '--'}</TableCell>}
                {columns.checkout && <TableCell sx={{ color: '#c62828', fontWeight: 500 }}>{row.checkout || '--'}</TableCell>}
                {columns.twh && <TableCell sx={{ color: '#555' }}>{row.total_worked_minutes || 0} m</TableCell>}
                {columns.toh && <TableCell sx={{ color: '#555' }}>{row.total_office_minutes || 0} m</TableCell>}
                {columns.status && (
                  <TableCell>
                    <Chip size="small" label={row.status} sx={{ bgcolor: row.status === 'DLOP' || row.status === 'LOP' ? '#ffebee' : '#e3f2fd', color: row.status === 'DLOP' || row.status === 'LOP' ? '#c62828' : '#1565c0', fontWeight: 'bold', px: 0.5 }} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} sx={{ borderTop: '1px solid #eee' }} />
      </Paper>
    </Box>
  );
};

export default AttendanceReportList;